import { useEffect, useMemo, useState } from "react";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Input } from "~/components/input";
import { useAuth } from "~/contexts/auth";
import { ClusterOperationsCommandPalette } from "~/routes/_index/components/ClusterOperationsCommandPalette";
import type { Cluster, Fleet, FleetSelector } from "~/types";
import { pluralize } from "~/utils";
import type { Wave } from "~/utils/fleet";
import { parseFleetTag, rolloutGroup } from "~/utils/rollout";

function cloneSelectors(selectors: FleetSelector[]): FleetSelector[] {
  return selectors.map((selector) =>
    "tag" in selector ? { tag: selector.tag } : { ids: [...selector.ids] },
  );
}

function selectorLabel(selector: FleetSelector): string {
  return "tag" in selector ? selector.tag : `ids (${selector.ids.length})`;
}

function selectorsEqual(a: FleetSelector[], b: FleetSelector[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function FleetsSection({ clusters }: { clusters: Cluster[] }) {
  const { setIsAuthorized } = useAuth();
  const [fleets, setFleets] = useState<Fleet[]>([]);
  // The one open editor: an existing fleet's name, or "create" mode.
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [draft, setDraft] = useState<FleetSelector[]>([]);
  const [newTag, setNewTag] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fleetToRun, setFleetToRun] = useState<{
    name: string;
    waves: Wave[];
  } | null>(null);

  useEffect(() => {
    let active = true;
    api
      .getFleets()
      .then((nextFleets) => {
        if (active) setFleets(nextFleets);
      })
      .catch((err) => {
        if (active) {
          if (err.status === 401) {
            setIsAuthorized(false);
          } else {
            setLoadError(
              err instanceof Error ? err.message : "Failed to fetch fleets",
            );
          }
        }
      });
    return () => {
      active = false;
    };
  }, [setIsAuthorized]);

  const expandedFleet = fleets.find((fleet) => fleet.name === expandedName);
  // PUT is an upsert, so a duplicate name on create would overwrite; the
  // server validates everything else at save.
  const validNewName =
    newName.trim() !== "" && !fleets.some((fleet) => fleet.name === newName);
  const dirty = creating
    ? draft.length > 0 || newName !== ""
    : expandedFleet
      ? !selectorsEqual(draft, expandedFleet.selectors)
      : false;
  const saveDisabled = creating ? !validNewName || draft.length === 0 : !dirty;

  const openEditor = (fleet: Fleet) => {
    setExpandedName(fleet.name);
    setCreating(false);
    setDraft(cloneSelectors(fleet.selectors));
    setNewTag("");
    setInlineError(null);
  };

  const openCreate = () => {
    setExpandedName(null);
    setCreating(true);
    setNewName("");
    setDraft([]);
    setNewTag("");
    setInlineError(null);
  };

  const closeEditor = () => {
    setExpandedName(null);
    setCreating(false);
  };

  const moveSelector = (index: number, offset: -1 | 1) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= draft.length) return;
    setDraft((current) => {
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
    setInlineError(null);
  };

  const removeSelector = (index: number) => {
    setDraft((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setInlineError(null);
  };

  const runClusters = useMemo(() => {
    if (!fleetToRun) return [];
    const ids = new Set(fleetToRun.waves.flatMap((wave) => wave.clusters));
    return clusters.filter((cluster) => ids.has(cluster.id));
  }, [fleetToRun, clusters]);

  const runOperation = useMemo(
    () =>
      fleetToRun
        ? {
            payload: { fleet: fleetToRun.name },
            waves: fleetToRun.waves,
            description: `${fleetToRun.name} fleet operation`,
          }
        : undefined,
    [fleetToRun],
  );

  const runFleet = async (name: string) => {
    setInlineError(null);
    try {
      const resolved = await api.resolveFleet(name);
      if (resolved.waves.length === 0) {
        setInlineError(`${name} resolves to no clusters`);
        return;
      }
      setFleetToRun({ name, waves: resolved.waves });
    } catch (err) {
      setInlineError(
        err instanceof Error ? err.message : "Failed to resolve fleet",
      );
    }
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    if (!parseFleetTag(tag)) {
      setInlineError(`tag must be key:value, e.g. rollout:${tag}`);
      return;
    }
    setDraft((current) => [...current, { tag }]);
    setNewTag("");
    setInlineError(null);
  };

  const save = async () => {
    const name = creating ? newName : expandedName;
    if (!name) return;
    setSaving(true);
    setInlineError(null);
    try {
      const saved = await api.putFleet(name, draft);
      setFleets((current) =>
        current.some((fleet) => fleet.name === saved.name)
          ? current.map((fleet) => (fleet.name === saved.name ? saved : fleet))
          : [...current, saved],
      );
      closeEditor();
    } catch (err) {
      if (err instanceof Error && "status" in err && err.status === 401) {
        setIsAuthorized(false);
      } else {
        setInlineError(
          err instanceof Error ? err.message : "Failed to update fleet",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const editorBody = (
    <div className="mt-2 border-t border-gray-100 pt-2 text-sm">
      <div className="flex flex-col gap-1">
        {draft.length === 0 ? (
          <p className="px-1 py-1 text-xs text-gray-400">
            no selectors yet — add tags below, first tag is the first wave
          </p>
        ) : null}
        {draft.map((selector, index) => {
          const label = selectorLabel(selector);
          const count =
            "tag" in selector
              ? clusters.filter((cluster) => {
                  const parsed = parseFleetTag(selector.tag);
                  if (!parsed) return false;
                  return parsed.key === "rollout"
                    ? rolloutGroup(cluster) === parsed.value
                    : cluster.tags?.[parsed.key] === parsed.value;
                }).length
              : null;
          return (
            <div
              key={index}
              className="flex min-h-8 items-center justify-between gap-3 px-1"
            >
              <span>
                {label}
                {count != null ? (
                  <span className="ml-2 text-xs tabular-nums text-gray-400">
                    {pluralize(count, "cluster")}
                  </span>
                ) : null}
              </span>
              <span className="flex shrink-0">
                <button
                  type="button"
                  aria-label={`move ${label} up`}
                  disabled={index === 0}
                  onClick={() => moveSelector(index, -1)}
                  className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:text-gray-200"
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`move ${label} down`}
                  disabled={index === draft.length - 1}
                  onClick={() => moveSelector(index, 1)}
                  className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:text-gray-200"
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label={`remove ${label}`}
                  onClick={() => removeSelector(index)}
                  className="px-2 py-1 text-gray-500 hover:text-red-600"
                >
                  ×
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <form
        className="mt-2 flex items-center gap-2 border-t border-gray-100 px-1 pt-2"
        onSubmit={(event) => {
          event.preventDefault();
          addTag();
        }}
      >
        <Input
          aria-label="new fleet tag"
          inputSize="sm"
          value={newTag}
          onChange={(event) => {
            setNewTag(event.target.value);
            setInlineError(null);
          }}
          placeholder="add tag (key:value)"
          autoComplete="off"
          className="flex-1"
        />
        <Button type="submit" variant="ghost" size="sm">
          add
        </Button>
      </form>
      {inlineError ? (
        <p className="px-1 pt-1 text-sm text-red-500">{inlineError}</p>
      ) : null}

      {/* Draft-only until save: nothing hits the server without the explicit
          confirmation below. */}
      <div className="mt-2 flex items-center gap-2 px-1">
        {dirty ? (
          <span className="text-xs text-gray-400">unsaved changes</span>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={saving}
          onClick={closeEditor}
          className="ml-auto"
        >
          cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={saving || saveDisabled}
          onClick={save}
        >
          {saving ? "saving…" : "save"}
        </Button>
      </div>
    </div>
  );

  return (
    <section className="font-mono">
      {loadError ? (
        <p className="px-1 text-sm text-red-500">{loadError}</p>
      ) : (
        <div className="flex flex-col space-y-2">
          {fleets.length === 0 && !creating ? (
            <p className="px-1 text-sm text-gray-500">No fleets configured.</p>
          ) : null}
          {fleets.map((fleet) => {
            const expanded = fleet === expandedFleet;
            return (
              <div
                key={fleet.name}
                className="rounded border border-gray-200 p-2"
              >
                <div className="flex w-full items-baseline gap-3 text-sm">
                  <span className="font-bold">{fleet.name}</span>
                  <span className="text-gray-500">
                    {fleet.selectors.map(selectorLabel).join(" → ")}
                  </span>
                  {!expanded ? (
                    <span className="ml-auto flex shrink-0 items-baseline gap-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="link"
                        aria-label={`run ${fleet.name}`}
                        onClick={() => runFleet(fleet.name)}
                        className="text-gray-500"
                      >
                        run
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="link"
                        aria-label={`edit ${fleet.name}`}
                        onClick={() => openEditor(fleet)}
                        className="text-gray-500"
                      >
                        edit
                      </Button>
                    </span>
                  ) : null}
                </div>

                {expanded ? editorBody : null}
              </div>
            );
          })}
          {creating ? (
            <div className="rounded border border-gray-200 p-2">
              <div className="flex w-full items-baseline gap-3 text-sm">
                <Input
                  aria-label="new fleet name"
                  inputSize="sm"
                  value={newName}
                  onChange={(event) => {
                    setNewName(event.target.value);
                    setInlineError(null);
                  }}
                  placeholder="fleet name"
                  autoComplete="off"
                  className="max-w-xs flex-1"
                />
                <span className="text-xs text-gray-400">new fleet</span>
              </div>
              {editorBody}
            </div>
          ) : (
            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={openCreate}
                className="text-gray-500"
              >
                add fleet
              </Button>
            </div>
          )}
        </div>
      )}
      {fleetToRun ? (
        <ClusterOperationsCommandPalette
          isOpen
          setIsOpen={(open) => {
            if (!open) setFleetToRun(null);
          }}
          clusters={runClusters}
          fleetOperation={runOperation!}
        />
      ) : null}
    </section>
  );
}
